import { Filter, Pencil } from "lucide-react";
import type { PaperFilters, SortField } from "@/hooks/useFilteredPapers";
import type { PaperTag } from "@/types/paper";
import { useNavigate } from "@tanstack/react-router";

interface Props {
  filters: PaperFilters;
  sortField?: SortField;
  conceptId: string;
  tags?: PaperTag[];
  /** When provided, the chip renders the concept name in quotes as context. */
  conceptName?: string;
}

function describeFilters(filters: PaperFilters, sortField?: SortField, tags?: PaperTag[]): string[] {
  const parts: string[] = [];
  if (filters.minScore) parts.push(`score \u2265 ${filters.minScore}`);
  if (filters.earliestYear) parts.push(`${filters.earliestYear}+`);
  if (filters.minCitations) parts.push(`\u2265 ${filters.minCitations} cites`);
  if (filters.isInfluential) parts.push("influential");
  if (filters.contributionType) parts.push(filters.contributionType);
  if (filters.argumentationRole) parts.push(filters.argumentationRole);

  if (filters.overrideFilter && filters.overrideFilter !== "active") {
    const labels: Record<string, string> = { championed: "marked relevant", dismissed: "marked not relevant", none: "no override" };
    if (labels[filters.overrideFilter]) parts.push(labels[filters.overrideFilter]);
  }
  if (filters.readingStatus) parts.push(filters.readingStatus);
  if (filters.tagIds && filters.tagIds.length > 0) {
    if (tags) {
      const tagMap = new Map(tags.map((t) => [t.id, t.name]));
      const names = filters.tagIds.map((id) => tagMap.get(id) ?? "tag").join(", ");
      parts.push(`tag: ${names}`);
    } else {
      parts.push("tagged");
    }
  }
  if (sortField) {
    const labels: Record<string, string> = {
      reading_priority: "reading priority",
      relevance_score: "score",
      citations: "citations",
      year: "year",
      title: "title",
    };
    parts.push(`sorted by ${labels[sortField] ?? sortField}`);
  }
  return parts;
}

export function ActiveFiltersBadge({
  filters,
  sortField,
  conceptId,
  tags,
  conceptName,
}: Props) {
  const navigate = useNavigate();
  const parts = describeFilters(filters, sortField, tags);
  const hasFilters = parts.length > 0;

  return (
    <div
      className="flex min-w-0 cursor-pointer items-center gap-1.5 rounded-md border border-dashed px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary"
      onClick={() =>
        navigate({
          to: "/concepts/$conceptId/curate",
          params: { conceptId },
        })
      }
      title="Click to edit filters on the Expand page"
    >
      <Filter className="size-3 shrink-0" />
      {conceptName && (
        <span className="truncate font-medium text-foreground">
          “{conceptName}”
        </span>
      )}
      {conceptName && <span className="shrink-0">·</span>}
      <span className="truncate">
        {hasFilters ? `Filtered: ${parts.join(", ")}` : "All in scope"}
      </span>
      <Pencil className="size-3 shrink-0" />
    </div>
  );
}
